import { useState, useEffect } from 'react'
import {
  Box,
  Card,
  Typography,
  Stack,
  Divider,
  TextField,
  FormControl,
  FormLabel,
  Alert,
  AlertTitle,
  Grid,
  ImageList,
  ImageListItem,
  Paper,
  IconButton,
  Button,
} from '@material-ui/core'
import { experimentalStyled as styled } from '@material-ui/core/styles'
import axios from 'axios'
import { REST_API_END_POINT } from 'src/constants/Defaultvalues'
import moment from 'moment'
import Scrollbar from 'src/components/Scrollbar'
import { PictureAsPdf } from '@material-ui/icons'
import { Icon } from '@iconify/react'
import filePdfIcon from '@iconify/icons-mdi/file-pdf'
import { DialogAnimate } from 'src/components/animate'
import { useTheme } from '@material-ui/core/styles'

const LabelStyle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}))

const LabelStyleHead = styled(Typography)(({ theme }) => ({
  ...theme.typography.h5,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
}))

export default function ViewDesignFormSubmissions({ designOrderId }) {
  const theme = useTheme()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedPDF, setSelectedPDF] = useState(null)
  const [openPDFModal, setOpenPDFModal] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(true)
  const [useOfficeViewer, setUseOfficeViewer] = useState(true)
  
  // Get user data for PDF viewing
  const user = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || '{}') : {}
  const isClient = user?.isClient
  const userType = isClient ? 3 : user?.user_type

  useEffect(() => {
    if (designOrderId) {
      console.log('[ViewDesignFormSubmissions] Fetching submissions for design order:', designOrderId)
      fetchTemplatesWithSubmissions()
    } else {
      console.warn('[ViewDesignFormSubmissions] No designOrderId provided')
      setLoading(false)
    }
  }, [designOrderId])

  // Fetch all templates with submissions in a single API call (batch API)
  const fetchTemplatesWithSubmissions = async () => {
    try {
      setLoading(true)
      // Single API call to get all templates with their saved values
      const res = await axios.post(
        `${REST_API_END_POINT}design-form/get-all-templates-with-values`,
        {
          designOrderId: designOrderId,
          module: 1
        }
      )

      if (res.data.status === 1) {
        const templatesWithData = res.data.data || []
        console.log('[ViewDesignFormSubmissions] Found templates with submissions:', templatesWithData.length)
        setTemplates(templatesWithData)
      } else {
        setTemplates([])
      }
    } catch (error) {
      console.error('Error fetching templates with submissions:', error)
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }

  // Helper to render HTML content
  const renderContent = (htmlContent) => {
    if (!htmlContent) return ''
    return (
      <div
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    )
  }

  // Common field wrapper style
  const fieldWrapperStyle = {
    marginBottom: '16px',
  }

  // Label style
  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
  }

  const renderFieldValue = (field) => {
    const { name, content, html_tag, value, savedValues, options } = field

    switch (html_tag) {
      case 'text':
      case 'textarea':
        return (
          <Box key={field.field_id} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={value || ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )

      case 'number':
        return (
          <Box key={field.field_id} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              size="small"
              value={value || ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )

      case 'date':
        // Handle date value - could be Unix timestamp, date string, or formatted date
        let dateValue = ''
        if (value) {
          if (typeof value === 'number' || /^\d+$/.test(value)) {
            // Unix timestamp
            dateValue = moment.unix(value).format('MM/DD/YYYY')
          } else if (moment(value).isValid()) {
            // Date string
            dateValue = moment(value).format('MM/DD/YYYY')
          } else {
            dateValue = value
          }
        }
        return (
          <Box key={field.field_id} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={dateValue}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )

      case 'select':
      case 'autocomplete':
        return (
          <Box key={field.field_id} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={value || ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )

      case 'radio-group':
        return (
          <Box key={field.field_id} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={value || ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )

      case 'checkbox-group':
        return (
          <Box key={field.field_id} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={Array.isArray(savedValues) ? savedValues.join(', ') : value || ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )

      case 'file':
        const fileUrls = savedValues && Array.isArray(savedValues) ? savedValues : value ? [value] : []
        const files = fileUrls.filter((url) => url)

        return (
          <Box 
            key={field.field_id} 
            sx={{
              ...fieldWrapperStyle,
              width: '100%',
              marginBottom: '24px',
            }}
          >
            <Box sx={labelStyle}>
              {renderContent(content)}
            </Box>
            <Box 
              sx={{ 
                marginTop: '12px',
                width: '100%',
              }}
            >
              {files.length > 0 ? (
                <ImageList 
                  gap={8} 
                  cols={3}
                  sx={{
                    margin: 0,
                    '& .MuiImageListItem-root': {
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }
                  }}
                >
                  {files.map((url, index) => {
                    const isImage = url.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) || 
                                   url.includes('image') ||
                                   !url.match(/\.(pdf)$/i)
                    const isPdf = url.match(/\.(pdf)$/i) || url.includes('pdf') || url.includes('application/pdf')

                    return (
                      <ImageListItem key={index}>
                        {isImage ? (
                          <Paper
                            component="img"
                            src={url}
                            alt={`File ${index + 1}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              cursor: 'pointer',
                            }}
                            onClick={() => window.open(url, '_blank')}
                          />
                        ) : isPdf ? (
                          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                            <Paper
                              sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setPdfLoading(true)
                                setSelectedPDF(url)
                                setOpenPDFModal(true)
                                setUseOfficeViewer(true)
                              }}
                            >
                              <IconButton
                                sx={{
                                  color: theme.palette.error.main,
                                }}
                              >
                                <PictureAsPdf style={{ fontSize: 40 }} />
                              </IconButton>
                            </Paper>
                          </Box>
                        ) : (
                          <Paper
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              cursor: 'pointer',
                            }}
                            onClick={() => window.open(url, '_blank')}
                          >
                            <Typography variant="caption">File {index + 1}</Typography>
                          </Paper>
                        )}
                      </ImageListItem>
                    )
                  })}
                </ImageList>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No files uploaded
                </Typography>
              )}
            </Box>
          </Box>
        )

      case 'paragraph':
        return (
          <Typography
            key={field.field_id}
            variant="body2"
            sx={{
              marginBottom: '12px',
              color: '#666',
              lineHeight: 1.6,
            }}
          >
            {renderContent(content)}
          </Typography>
        )

      case 'header':
        return (
          <Typography
            key={field.field_id}
            variant="body1"
            sx={{
              color: '#333',
              fontWeight: 600,
              margin: '0 0 5px 0',
              fontSize: '14px',
            }}
          >
            {renderContent(content)}
          </Typography>
        )

      default:
        return (
          <Box key={field.field_id} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={value || ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )
    }
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading form submissions...
        </Typography>
      </Box>
    )
  }

  if (templates.length === 0) {
    return (
      <Card sx={{ p: 3, borderRadius: '8px' }}>
        <Alert variant="outlined" severity="info" sx={{ my: 2 }}>
          <AlertTitle>No Form Submissions</AlertTitle>
          <Typography variant="body2">
            No design form submissions found for this design order. Forms will appear here once they are filled and saved during order creation.
          </Typography>
        </Alert>
      </Card>
    )
  }

  return (
    <Box>
      <Stack spacing={3}>
        {templates.map((template, index) => {
          // Convert fields array and separate file fields
          const fieldsArray = template.fields || []
          const fileFields = fieldsArray.filter(field => field.html_tag === 'file')
          const otherFields = fieldsArray.filter(field => field.html_tag !== 'file')
          
          // Group non-file fields into two columns
          const midPoint = Math.ceil(otherFields.length / 2)
          const leftColumn = otherFields.slice(0, midPoint)
          const rightColumn = otherFields.slice(midPoint)

          return (
            <Box
              key={template.id}
              sx={{
                backgroundColor: '#fff',
                position: 'relative',
                paddingBottom: '20px',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              {/* Red Header Banner */}
              <Box
                sx={{
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  padding: '16px 24px',
                  marginBottom: '24px',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    margin: 0,
                    textAlign: 'center',
                  }}
                >
                  {template.name}:
                </Typography>
              </Box>

              {/* Form Content with Two Columns */}
              <Box sx={{ padding: '0 24px 24px 24px' }}>
                <Grid container spacing={3} alignItems="flex-start">
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2} sx={{ marginTop: 0 }}>
                      {leftColumn.map((field) => renderFieldValue(field))}
                    </Stack>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2} sx={{ marginTop: 0 }}>
                      {rightColumn.map((field) => renderFieldValue(field))}
                    </Stack>
                  </Grid>
                  
                  {/* File upload fields - full width below columns */}
                  {fileFields.length > 0 && (
                    <Grid item xs={12}>
                      <Stack spacing={2}>
                        {fileFields.map((field) => renderFieldValue(field))}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )
        })}
      </Stack>

      {/* PDF Modal */}
      <DialogAnimate open={openPDFModal} onClose={() => setOpenPDFModal(false)}>
        <Card sx={{ p: 3, maxWidth: 768, margin: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                if (isClient || userType === 3) {
                  const newWindow = window.open('', '_blank')
                  newWindow.document.write(`
                    <html>
                      <head>
                        <title>PDF Viewer</title>
                        <style>
                          body { margin: 0; padding: 0; overflow: hidden; }
                          iframe { width: 100vw; height: 100vh; border: none; }
                        </style>
                      </head>
                      <body>
                        <iframe src="${selectedPDF}#toolbar=0&navpanes=0&scrollbar=0" title="PDF Viewer"></iframe>
                      </body>
                    </html>
                  `)
                  newWindow.document.close()
                } else {
                  window.open(selectedPDF, '_blank')
                }
              }}
              disabled={!selectedPDF}
              startIcon={<Icon icon={filePdfIcon} width={20} height={20} />}
            >
              View PDF
            </Button>
          </Box>
          {pdfLoading && (
            <Box sx={{ width: '100%', height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography>Loading PDF...</Typography>
            </Box>
          )}
          {(isClient || userType === 3) && useOfficeViewer ? (
            <iframe
              src={`https://docs.google.com/gview?url=${encodeURIComponent(selectedPDF)}&embedded=true&toolbar=0&navpanes=0&scrollbar=0`}
              title="Selected PDF"
              width="100%"
              height="500px"
              frameBorder="0"
              style={{ display: pdfLoading ? 'none' : 'block' }}
              onLoad={() => setPdfLoading(false)}
              onError={() => {
                setUseOfficeViewer(false)
                setPdfLoading(false)
              }}
            />
          ) : (
            <iframe
              src={selectedPDF}
              title="Selected PDF"
              width="100%"
              height="500px"
              frameBorder="0"
              style={{ display: pdfLoading ? 'none' : 'block' }}
              onLoad={() => setPdfLoading(false)}
            />
          )}
        </Card>
      </DialogAnimate>
    </Box>
  )
}
